import { datadogRum } from "@datadog/browser-rum";

import { sanitizeEvent } from "../sanitize-event";

interface User {
  id?: string;
  name?: string;
  email?: string;
  [key: string]: unknown;
}

interface ErrorTrackingOptions {
  applicationId: string;
  clientToken: string;
  service: string;
  version: string;
  mode: string;
}

let isInitialized = false;

function initializeErrorTracking(options: ErrorTrackingOptions): void {
  const debug = options.mode !== "production";

  if (isInitialized) {
    if (debug) {
      console.warn("[ErrorTracking] Already initialized");
    }
    return;
  }

  if (!options.applicationId || !options.clientToken) {
    if (debug) {
      console.warn(
        "[ErrorTracking] Missing required Datadog configuration, skipping initialization"
      );
    }
    return;
  }

  try {
    datadogRum.init({
      applicationId: options.applicationId,
      clientToken: options.clientToken,
      site: "datadoghq.eu",
      service: options.service,
      env: options.mode,
      version: options.version,
      beforeSend: sanitizeEvent,
    });

    if (debug) {
      console.info("[ErrorTracking] Datadog RUM initialized successfully");
    }

    isInitialized = true;
  } catch (error) {
    console.error("[ErrorTracking] Failed to initialize Datadog RUM:", error);
  }
}

function trackError(error: unknown, context?: object): void {
  if (!isInitialized) {
    console.error(
      "[ErrorTracking] Not initialized, cannot track error:",
      error
    );
    return;
  }

  try {
    datadogRum.addError(error, context);
  } catch (trackingError) {
    console.error("[ErrorTracking] Failed to track error:", trackingError);
  }
}

function setUser(user: User): void {
  if (!isInitialized) {
    console.warn("[ErrorTracking] Not initialized, cannot set user");
    return;
  }

  try {
    datadogRum.setUser(user);
  } catch (error) {
    console.error("[ErrorTracking] Failed to set user:", error);
  }
}

function isErrorTrackingInitialized(): boolean {
  return isInitialized;
}

export type { ErrorTrackingOptions, User };
export {
  initializeErrorTracking,
  trackError,
  setUser,
  isErrorTrackingInitialized,
};
